import React from 'react'
import { Ingredient } from '../types/recipe'
import { APP_SETTINGS } from '../../../constants/settings'

interface RecipeIngredientsProps {
  ingredients: Ingredient[]
  servings: number
  originalServings: number
  hasTouchScreen: boolean
}

const RecipeIngredients: React.FC<RecipeIngredientsProps> = ({
  ingredients,
  servings,
  originalServings,
  hasTouchScreen
}) => {
  // Use different font sizes based on device type
  const fontSize = hasTouchScreen ? 
    APP_SETTINGS.TOUCH_FONT_SIZE : 
    APP_SETTINGS.INGREDIENTS_FONT_SIZE
  
  // Adjust ingredient quantities based on serving size
  const adjustedIngredients = ingredients.map(ingredient => {
    if (typeof ingredient.quantity === 'number') {
      const adjustedQuantity = (ingredient.quantity * servings / originalServings)
      
      // Format the number to avoid long decimals
      const formattedQuantity = adjustedQuantity % 1 === 0 
        ? adjustedQuantity.toString() 
        : adjustedQuantity.toFixed(1).replace(/\.0$/, '')
      
      return {
        ...ingredient,
        displayQuantity: formattedQuantity
      }
    }
    
    return {
      ...ingredient,
      displayQuantity: ingredient.quantity
    }
  })
  
  return (
    <div 
      className="w-full h-full flex flex-col p-8 bg-gray-900"
      style={{ fontSize: `${fontSize}px` }}
    >
      <h2 
        className="text-3xl font-semibold text-white mb-6"
        style={{ fontSize: `${fontSize + 4}px` }}
      >
        Ingredients
        {servings && (
          <span className="text-gray-400 ml-2">
            (for {servings} {servings === 1 ? 'serving' : 'servings'})
          </span>
        )}
      </h2>
      
      <div className="bg-gray-800 rounded-lg p-6 shadow-lg flex-grow overflow-y-auto">
        <ul className="space-y-4">
          {adjustedIngredients.map((ingredient) => (
            <li 
              key={ingredient.id} 
              className="flex items-baseline pb-2 border-b border-gray-700 last:border-b-0"
            >
              <div className="w-1/3 text-gray-300 font-medium">
                {ingredient.displayQuantity} {ingredient.unit}
              </div>
              <div className="w-2/3 text-white">
                {ingredient.name}
                {ingredient.notes && (
                  <span className="text-gray-400 ml-2 italic">
                    ({ingredient.notes})
                  </span>
                )}
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}

export default RecipeIngredients
